// ============================================================================
// TOURNAMENT TYPES - Extended for PokerSweepstars
// Mon/Wed/Fri 5:30 PM PST • 10% House Rake • Top 3 Payouts (50/30/20)
// Registration ALWAYS OPEN until tournament starts
// ============================================================================

import { Timestamp } from 'firebase/firestore';

// ============================================================================
// BASE TYPES
// ============================================================================

export type TournamentStatus = 
  | 'scheduled'    // Future tournament, registration not yet open
  | 'upcoming'     // Legacy - same as scheduled
  | 'registering'  // Registration is open (ALWAYS open until start)
  | 'starting'     // 5-minute countdown before start
  | 'late_reg'     // Tournament started, late registration open until level 4
  | 'running'      // Tournament running, late reg closed
  | 'in_progress'  // Same as running
  | 'final_table'  // Down to final table (9 or fewer)
  | 'heads_up'     // Down to 2 players
  | 'completed'    // Tournament finished
  | 'cancelled';   // Cancelled (not enough players or admin cancelled)

export type TournamentType = 'freezeout' | 'rebuy' | 'knockout' | 'satellite' | 'freeroll';
export type CurrencyType = 'playcoins' | 'pkcash';
//  Added PlayCoins tiers for restricted state users

export type TournamentTier = 'low' | 'mid' | 'high' | 'special' | 'pc_low' | 'pc_mid' | 'pc_high';

// ============================================================================
// BLIND LEVEL STRUCTURE (10-min levels, antes start level 4)
// ============================================================================

export interface BlindLevel {
  level: number;
  smallBlind: number;
  bigBlind: number;
  ante?: number;
  duration?: number; // minutes (default 10)
}

// 20 levels for longer tournaments
export const DEFAULT_BLIND_LEVELS: BlindLevel[] = [
  { level: 1, smallBlind: 25, bigBlind: 50, ante: 0, duration: 10 },
  { level: 2, smallBlind: 50, bigBlind: 100, ante: 0, duration: 10 },
  { level: 3, smallBlind: 75, bigBlind: 150, ante: 0, duration: 10 },
  { level: 4, smallBlind: 100, bigBlind: 200, ante: 25, duration: 10 },
  { level: 5, smallBlind: 150, bigBlind: 300, ante: 50, duration: 10 },
  { level: 6, smallBlind: 200, bigBlind: 400, ante: 50, duration: 10 },
  { level: 7, smallBlind: 300, bigBlind: 600, ante: 75, duration: 10 },
  { level: 8, smallBlind: 400, bigBlind: 800, ante: 100, duration: 10 },
  { level: 9, smallBlind: 500, bigBlind: 1000, ante: 125, duration: 10 },
  { level: 10, smallBlind: 600, bigBlind: 1200, ante: 150, duration: 10 },
  { level: 11, smallBlind: 800, bigBlind: 1600, ante: 200, duration: 10 },
  { level: 12, smallBlind: 1000, bigBlind: 2000, ante: 250, duration: 10 },
  { level: 13, smallBlind: 1500, bigBlind: 3000, ante: 375, duration: 10 },
  { level: 14, smallBlind: 2000, bigBlind: 4000, ante: 500, duration: 10 },
  { level: 15, smallBlind: 3000, bigBlind: 6000, ante: 750, duration: 10 },
  { level: 16, smallBlind: 4000, bigBlind: 8000, ante: 1000, duration: 10 },
  { level: 17, smallBlind: 5000, bigBlind: 10000, ante: 1250, duration: 10 },
  { level: 18, smallBlind: 6000, bigBlind: 12000, ante: 1500, duration: 10 },
  { level: 19, smallBlind: 8000, bigBlind: 16000, ante: 2000, duration: 10 },
  { level: 20, smallBlind: 10000, bigBlind: 20000, ante: 2500, duration: 10 },
];

// ============================================================================
// PAYOUT STRUCTURES
// ============================================================================

export interface PayoutLevel {
  position: number;
  percentage: number;
  minPayout?: number;
}

// Top 3 - Default for all regular tournaments (50/30/20)
export const DEFAULT_PAYOUT_TOP_3: PayoutLevel[] = [
  { position: 1, percentage: 50 },
  { position: 2, percentage: 30 },
  { position: 3, percentage: 20 },
];

// Heads-up only (2 players)
export const PAYOUT_HEADS_UP: PayoutLevel[] = [
  { position: 1, percentage: 70 },
  { position: 2, percentage: 30 },
];

// 9 players
export const DEFAULT_PAYOUT_9_PLAYERS: PayoutLevel[] = [
  { position: 1, percentage: 50 },
  { position: 2, percentage: 30 },
  { position: 3, percentage: 20 },
];

// 18+ players
export const DEFAULT_PAYOUT_18_PLAYERS: PayoutLevel[] = [
  { position: 1, percentage: 40 },
  { position: 2, percentage: 25 },
  { position: 3, percentage: 18 },
  { position: 4, percentage: 10 },
  { position: 5, percentage: 7 },
];

// 45+ players
export const DEFAULT_PAYOUT_45_PLAYERS: PayoutLevel[] = [
  { position: 1, percentage: 30 },
  { position: 2, percentage: 20 },
  { position: 3, percentage: 14 },
  { position: 4, percentage: 10 },
  { position: 5, percentage: 8 },
  { position: 6, percentage: 6 },
  { position: 7, percentage: 5 },
  { position: 8, percentage: 4 },
  { position: 9, percentage: 3 },
];

// ============================================================================
// TOURNAMENT TIER CONFIGURATION - UNLIMITED PLAYERS
// ============================================================================

export interface TournamentTierConfig {
  id: TournamentTier;
  name: string;
  displayName: string;
  icon: string;
  buyIn: number;
  currency: CurrencyType;
  houseRakePercent: number;         // 10% of prize pool
  startingChips: number;
  minPlayersToStart: number;        // 5 minimum
  maxPlayersPerTable: number;       // 9 players per table
  lateRegLevels: number;            // Late registration until level 4 (40 min)
  blindLevelDuration: number;       // 10 minutes
  decisionTimerSeconds: number;     // 15 seconds
  timeBankSeconds: number;          // +10 seconds bonus
  timeBankUsesPerHand: number;      // 1 use per hand
  timeBankAppearAtSeconds: number;  // Appears at 5 seconds remaining
  color: string;
  gradient: string;
}

export const TOURNAMENT_TIERS: Record<TournamentTier, TournamentTierConfig> = {
  low: {
    id: 'low',
    name: 'daily-grind',
    displayName: ' Daily Grind',

    icon: '',

    buyIn: 5,
    currency: 'pkcash',
    houseRakePercent: 10,
    startingChips: 5000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#CD7F32',
    gradient: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
  },
  mid: {
    id: 'mid',
    name: 'silver-stakes',
    displayName: ' Silver Stakes',

    icon: '',

    buyIn: 25,
    currency: 'pkcash',
    houseRakePercent: 10,
    startingChips: 10000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#C0C0C0',
    gradient: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)',
  },
  high: {
    id: 'high',
    name: 'diamond-series',
    displayName: ' Diamond Series',

    icon: '',

    buyIn: 100,
    currency: 'pkcash',
    houseRakePercent: 10,
    startingChips: 20000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  },
  special: {
    id: 'special',
    name: 'special-event',
    displayName: ' Special Event',

    icon: '',

    buyIn: 500,
    currency: 'pkcash',
    houseRakePercent: 10,
    startingChips: 50000,
    minPlayersToStart: 10,
    maxPlayersPerTable: 9,
    lateRegLevels: 6,
    blindLevelDuration: 15,
    decisionTimerSeconds: 20,
    timeBankSeconds: 15,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#00CED1',
    gradient: 'linear-gradient(135deg, #00CED1 0%, #008B8B 100%)',
  },
  // =========================================================================
  //  PLAYCOINS TIERS - For restricted state users

  // =========================================================================
  pc_low: {
    id: 'pc_low',
    name: 'playcoins-bronze',
    displayName: ' Bronze Freeroll',

    icon: '',

    buyIn: 500,
    currency: 'playcoins',
    houseRakePercent: 0, // No rake on PlayCoins
    startingChips: 5000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#CD7F32',
    gradient: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)',
  },
  pc_mid: {
    id: 'pc_mid',
    name: 'playcoins-silver',
    displayName: ' Silver Showdown',

    icon: '',

    buyIn: 2500,
    currency: 'playcoins',
    houseRakePercent: 0,
    startingChips: 10000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#C0C0C0',
    gradient: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)',
  },
  pc_high: {
    id: 'pc_high',
    name: 'playcoins-gold',
    displayName: ' Gold Championship',

    icon: '',

    buyIn: 10000,
    currency: 'playcoins',
    houseRakePercent: 0,
    startingChips: 20000,
    minPlayersToStart: 5,
    maxPlayersPerTable: 9,
    lateRegLevels: 4,
    blindLevelDuration: 10,
    decisionTimerSeconds: 15,
    timeBankSeconds: 10,
    timeBankUsesPerHand: 1,
    timeBankAppearAtSeconds: 5,
    color: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  },
};

// ============================================================================
// TOURNAMENT SCHEDULE - PKCash Mon/Wed/Fri, PlayCoins Tue/Thu/Sat @ 5:30 PM PST
// ============================================================================

export interface TournamentSchedule {
  dayOfWeek: number;        // 0 = Sunday, 1 = Monday, etc.
  hour: number;             // 24-hour format (PST)
  minute: number;
  tiers: TournamentTier[];  // Which tiers run at this time
}

export const TOURNAMENT_SCHEDULE: TournamentSchedule[] = [
  // PKCash Tournaments - Mon/Wed/Fri 5:30 PM PST
  { dayOfWeek: 1, hour: 17, minute: 30, tiers: ['low', 'mid', 'high'] }, // Monday
  { dayOfWeek: 3, hour: 17, minute: 30, tiers: ['low', 'mid', 'high'] }, // Wednesday
  { dayOfWeek: 5, hour: 17, minute: 30, tiers: ['low', 'mid', 'high'] }, // Friday
  //  PlayCoins Tournaments - Tue/Thu/Sat 5:30 PM PST (for restricted states)

  { dayOfWeek: 2, hour: 17, minute: 30, tiers: ['pc_low', 'pc_mid', 'pc_high'] }, // Tuesday
  { dayOfWeek: 4, hour: 17, minute: 30, tiers: ['pc_low', 'pc_mid', 'pc_high'] }, // Thursday
  { dayOfWeek: 6, hour: 17, minute: 30, tiers: ['pc_low', 'pc_mid', 'pc_high'] }, // Saturday
];

// ============================================================================
// TOURNAMENT RAKE CONFIGURATION
// ============================================================================

export interface TournamentRakeConfig {
  houseRakePercent: number;  // 10% of total buy-ins
  agentTiers: {
    standard: number;  // 10% of rake
    premium: number;   // 15% of rake
    vip: number;       // 20% of rake
  };
}

export const TOURNAMENT_RAKE_CONFIG: TournamentRakeConfig = {
  houseRakePercent: 10,
  agentTiers: {
    standard: 0.10,
    premium: 0.15,
    vip: 0.20,
  },
};

// ============================================================================
// TOURNAMENT RAKE TRANSACTION
// ============================================================================

export interface TournamentRakeTransaction {
  id?: string;
  tournamentId: string;
  tournamentName: string;
  tier: TournamentTier;
  currency: CurrencyType;
  totalBuyIns: number;
  totalRake: number;
  houseRake: number;
  agentPayouts: {
    agentId: string;
    amount: number;
    tier: 'standard' | 'premium' | 'vip';
    playerCount: number;
  }[];
  playerCount: number;
  timestamp: Date;
}

// ============================================================================
// MAIN TOURNAMENT INTERFACE
// ============================================================================

export interface Tournament {
  id: string;
  name: string;
  description?: string;
  tier?: TournamentTier;
  weekNumber?: number;
  
  // Schedule - Registration ALWAYS open until startTime
  startTime: Date;
  registrationOpenTime: Date;    // Set to creation time (always open)
  registrationCloseTime: Date;   // Same as startTime
  lateRegistrationEndTime?: Date | null;
  actualStartTime?: Date;
  endTime?: Date;
  
  // Buy-in & Prizes
  buyInAmount: number;
  currency: CurrencyType;
  entryFee: number;              // House rake portion (10% of buy-in)
  houseRakePercent?: number;     // 10%
  prizePool: number;
  guaranteedPrize?: number;
  payoutStructure: PayoutLevel[];
  
  // Rake tracking
  totalRakeCollected?: number;
  agentRakePaid?: number;
  houseRakeNet?: number;
  
  // Structure
  type: TournamentType;
  startingChips: number;
  blindLevels: BlindLevel[];
  blindLevelDuration: number;    // minutes
  currentBlindLevel?: number;
  lateRegLevels?: number;        // Late reg closes after this level
  
  // Timer Settings (15s + 10s time bank)
  decisionTimerSeconds?: number;
  timeBankSeconds?: number;
  timeBankUsesPerHand?: number;
  
  // Players - UNLIMITED (no max, only min to start)
  minPlayers: number;
  maxPlayers: number;            // Set very high for "unlimited"
  maxPlayersPerTable?: number;   // 9
  currentPlayers: number;
  registeredPlayers: string[];   // User IDs
  activePlayers?: string[];      // Still in tournament
  tablesCount?: number;
  
  // Status
  status: TournamentStatus;
  winnerId?: string;
  
  // Metadata
  createdAt: Date;
  createdBy: string;
  updatedAt: Date;
  isRecurring?: boolean;
  isFeatured?: boolean;
  
  // Creator-hosted tournaments (see functions /host/createTournament)
  hostId?: string;
  hostAgentId?: string | null;
  hostName?: string;
  isCreatorTournament?: boolean;
  entry?: 'code' | 'group' | 'open';
  inviteCode?: string | null;
  hostGroupId?: string | null;
  hostApproval?: 'auto' | 'pending' | 'approved' | 'rejected';
  media?: 'off' | 'voice' | 'video';
  mediaScope?: 'all' | 'final';
  mediaComped?: boolean;

  // Optional
  password?: string;
  rebuyAllowed?: boolean;
  rebuyLimit?: number;
  addonAllowed?: boolean;
  addonChips?: number;
  addonCost?: number;
}

// ============================================================================
// TOURNAMENT REGISTRATION
// ============================================================================

export interface TournamentRegistration {
  id: string;
  odD: string;
  odD: string;
  registeredAt: Date;
  buyInPaid: number;
  entryFeePaid?: number;
  chips: number;
  status: 'registered' | 'seated' | 'playing' | 'eliminated' | 'winner';
  isConnected?: boolean;
  autoFoldCount?: number;
  rebuys?: number;
  addons?: number;
  finishPosition?: number;
  prizeWon?: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate prize pool after house rake (10%)
 */
export function calculatePrizePool(
  totalBuyIns: number,
  houseRakePercent: number = 10
): { prizePool: number; houseRake: number } {
  const houseRake = Math.floor(totalBuyIns * (houseRakePercent / 100) * 100) / 100;
  const prizePool = Math.floor((totalBuyIns - houseRake) * 100) / 100;
  return { prizePool, houseRake };
}

/**
 * Calculate payouts based on prize pool and structure
 */
export function calculatePayouts(
  prizePool: number,
  payoutStructure: PayoutLevel[]
): { position: number; amount: number }[] {
  return payoutStructure.map((level) => ({
    position: level.position,
    amount: Math.floor(prizePool * (level.percentage / 100) * 100) / 100,
  }));
}

/**
 * Get appropriate payout structure based on player count
 */
export function getPayoutStructure(playerCount: number): PayoutLevel[] {
  if (playerCount <= 2) return PAYOUT_HEADS_UP;
  if (playerCount <= 9) return DEFAULT_PAYOUT_TOP_3;
  if (playerCount <= 18) return DEFAULT_PAYOUT_18_PLAYERS;
  return DEFAULT_PAYOUT_45_PLAYERS;
}

/**
 * Calculate number of tables needed
 */
export function calculateTablesNeeded(playerCount: number, maxPerTable: number = 9): number {
  if (playerCount === 0) return 0;
  return Math.ceil(playerCount / maxPerTable);
}

/**
 * Format time until tournament starts
 */
export function formatTimeUntilStart(startTime: Date): string {
  const now = new Date();
  const diff = new Date(startTime).getTime() - now.getTime();
  
  if (diff <= 0) return 'Starting now';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/**
 * Get status display info with colors
 */
export function getStatusDisplay(status: TournamentStatus): {
  label: string;
  color: string;
  bgColor: string;
} {
  const statusMap: Record<string, { label: string; color: string; bgColor: string }> = {
    scheduled: { label: 'Scheduled', color: '#718096', bgColor: '#71809620' },
    upcoming: { label: 'Upcoming', color: '#4299E1', bgColor: '#4299E120' },
    registering: { label: 'Registration Open', color: '#48BB78', bgColor: '#48BB7820' },

    starting: { label: '⏰ Starting Soon', color: '#F6AD55', bgColor: '#F6AD5520' },
    late_reg: { label: 'Late Reg Open', color: '#4299E1', bgColor: '#4299E120' },

    running: { label: 'In Progress', color: '#9F7AEA', bgColor: '#9F7AEA20' },

    in_progress: { label: 'In Progress', color: '#9F7AEA', bgColor: '#9F7AEA20' },

    final_table: { label: 'Final Table', color: '#FFD700', bgColor: '#FFD70020' },

    heads_up: { label: 'Heads Up', color: '#F56565', bgColor: '#F5656520' },

    completed: { label: 'Completed', color: '#A0AEC0', bgColor: '#A0AEC020' },

    cancelled: { label: 'Cancelled', color: '#F56565', bgColor: '#F5656520' },

  };
  return statusMap[status] || { label: 'Unknown', color: '#718096', bgColor: '#71809620' };
}

/**
 * Get day name from day of week number
 */
export function getDayName(dayOfWeek: number): string {
  return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek] || 'Unknown';
}

/**
 * Format time for display
 */
export function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get the next scheduled tournament date for a tier
 */
export function getNextTournamentDate(tier: TournamentTier): Date {
  const now = new Date();
  const schedules = TOURNAMENT_SCHEDULE.filter(s => s.tiers.includes(tier));
  let nextDate: Date | null = null;

  for (let daysAhead = 0; daysAhead <= 7; daysAhead++) {
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + daysAhead);

    for (const schedule of schedules) {
      if (checkDate.getDay() === schedule.dayOfWeek) {
        const tournamentDate = new Date(checkDate);
        tournamentDate.setHours(schedule.hour, schedule.minute, 0, 0);
        
        // If it's today but the time has passed, skip
        if (daysAhead === 0 && tournamentDate <= now) continue;
        
        if (!nextDate || tournamentDate < nextDate) {
          nextDate = tournamentDate;
        }
      }
    }
    if (nextDate) break;
  }

  return nextDate || new Date(now.getTime() + (3 * 24 * 60 * 60 * 1000));
}

/**
 *  Check if registration is OPEN for a tournament

 * Registration is ALWAYS open until tournament starts or during late_reg
 */
export function isRegistrationOpen(tournament: Tournament): boolean {
  const now = new Date();
  const startTime = new Date(tournament.startTime);
  
  // Registration open for these statuses
  const openStatuses: TournamentStatus[] = [
    'scheduled',
    'upcoming',
    'registering',
    'starting',
    'late_reg',
  ];
  
  // Check if status allows registration
  if (!openStatuses.includes(tournament.status)) {
    return false;
  }
  
  // For late_reg, check if we're still within allowed levels
  if (tournament.status === 'late_reg') {
    if (tournament.currentBlindLevel && tournament.lateRegLevels) {
      return tournament.currentBlindLevel <= tournament.lateRegLevels;
    }
    return true; // Default to open if we can't determine level
  }
  
  // For pre-start statuses, always open (tournament hasn't started yet)
  return true;
}

/**
 * Check if late registration is still open
 */
export function isLateRegOpen(tournament: Tournament): boolean {
  if (!['late_reg', 'running', 'in_progress'].includes(tournament.status)) return false;
  if (!tournament.currentBlindLevel || !tournament.lateRegLevels) return false;
  return tournament.currentBlindLevel <= tournament.lateRegLevels;
}

/**
 * Get tier config by buy-in amount
 */
export function getTierByBuyIn(buyIn: number): TournamentTierConfig | null {
  const tiers = Object.values(TOURNAMENT_TIERS);
  return tiers.find(t => t.buyIn === buyIn) || null;
}

/**
 * Get upcoming tournament dates for the next N days
 */
export function getUpcomingTournamentDates(days: number = 7): { date: Date; tier: TournamentTier }[] {
  const results: { date: Date; tier: TournamentTier }[] = [];
  const now = new Date();
  
  for (let daysAhead = 0; daysAhead < days; daysAhead++) {
    const checkDate = new Date(now);
    checkDate.setDate(checkDate.getDate() + daysAhead);
    
    for (const schedule of TOURNAMENT_SCHEDULE) {
      if (checkDate.getDay() === schedule.dayOfWeek) {
        const tournamentDate = new Date(checkDate);
        tournamentDate.setHours(schedule.hour, schedule.minute, 0, 0);
        
        // Skip if time has passed today
        if (daysAhead === 0 && tournamentDate <= now) continue;
        
        // Add each tier
        for (const tier of schedule.tiers) {
          results.push({ date: tournamentDate, tier });
        }
      }
    }
  }
  
  // Sort by date
  return results.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Generate tournament name based on tier and date
 */
export function generateTournamentName(tier: TournamentTier, date: Date): string {
  const config = TOURNAMENT_TIERS[tier];
  const dayName = getDayName(date.getDay());
  const weekNum = getWeekNumber(date);
  return `${config.displayName} - ${dayName} W${weekNum}`;
}

/**
 * Generate tournament description based on tier
 */
export function generateTournamentDescription(tier: TournamentTier): string {
  const config = TOURNAMENT_TIERS[tier];
  return `${config.displayName} tournament - $${config.buyIn} buy-in, ${config.startingChips.toLocaleString()} starting chips, 10-minute blind levels. Top 3 paid (50/30/20). 15s decision timer + 10s time bank.`;
}

/**
 * Get ISO week number
 */
export function getWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}